import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, Calendar, Sparkles, AlertCircle, ArrowRight } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { formatNumber, formatCurrency, formatPercent, calculateChange, formatDate } from '../utils/helpers';

const Dashboard = () => {
    const { analytics, insights, scheduledPosts, creatives, user, selectedBrand } = useApp();

    const stats = [
        {
            label: 'Total Impressions',
            value: formatNumber(analytics.overview.impressions.current),
            change: calculateChange(analytics.overview.impressions.current, analytics.overview.impressions.previous),
            icon: '👁️',
        },
        {
            label: 'Click-Through Rate',
            value: formatPercent((analytics.overview.clicks.current / analytics.overview.impressions.current) * 100),
            change: calculateChange(
                analytics.overview.clicks.current / analytics.overview.impressions.current,
                analytics.overview.clicks.previous / analytics.overview.impressions.previous
            ),
            icon: '🎯',
        },
        {
            label: 'Total Revenue',
            value: formatCurrency(analytics.overview.revenue.current),
            change: calculateChange(analytics.overview.revenue.current, analytics.overview.revenue.previous),
            icon: '💰',
        },
        {
            label: 'ROAS',
            value: (analytics.overview.revenue.current / analytics.overview.spend.current).toFixed(2) + 'x',
            change: calculateChange(
                analytics.overview.revenue.current / analytics.overview.spend.current,
                analytics.overview.revenue.previous / analytics.overview.spend.previous
            ),
            icon: '📈',
        },
    ];

    // Filter data by selected brand
    const brandFilteredCreatives = selectedBrand
        ? creatives.filter(c => c.brandId === selectedBrand.id)
        : creatives;

    const brandFilteredPosts = selectedBrand
        ? scheduledPosts.filter(p => p.brandId === selectedBrand.id)
        : scheduledPosts;

    const upcomingPosts = brandFilteredPosts.slice(0, 5);
    const recentCreatives = brandFilteredCreatives.slice(0, 4);
    const topInsights = insights.filter(i => i.priority === 'high').slice(0, 3);

    return (
        <div className="animate-fadeIn">
            {/* Header */}
            <div style={{ marginBottom: 'var(--space-8)' }}>
                <h1 style={{ fontSize: 'var(--font-size-4xl)', marginBottom: 'var(--space-2)' }}>
                    Welcome back, {user.name.split(' ')[0]}! 👋
                </h1>
                <p className="text-secondary">Here's what's happening with your campaigns today.</p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-4 gap-6" style={{ marginBottom: 'var(--space-8)' }}>
                {stats.map((stat, index) => (
                    <div key={index} className="stat-card">
                        <div style={{ fontSize: 'var(--font-size-2xl)', marginBottom: 'var(--space-2)' }}>
                            {stat.icon}
                        </div>
                        <div className="stat-value">{stat.value}</div>
                        <div className="stat-label">{stat.label}</div>
                        <div className={`stat-change ${stat.change >= 0 ? 'positive' : 'negative'}`}>
                            {stat.change >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                            {formatPercent(Math.abs(stat.change))} vs last period
                        </div>
                    </div>
                ))}
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-3 gap-6" style={{ marginBottom: 'var(--space-8)' }}>
                {/* AI Insights */}
                <div className="card" style={{ gridColumn: 'span 2' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
                        <h2 style={{ margin: 0 }}>🤖 AI Insights & Recommendations</h2>
                        <Link to="/insights" className="btn btn-sm btn-ghost">
                            View All <ArrowRight size={16} />
                        </Link>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                        {topInsights.map((insight) => (
                            <div
                                key={insight.id}
                                style={{
                                    padding: 'var(--space-4)',
                                    background: 'var(--color-bg-secondary)',
                                    borderRadius: 'var(--radius-lg)',
                                    borderLeft: '4px solid var(--color-primary)',
                                }}
                            >
                                <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-2)' }}>
                                    <AlertCircle size={20} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 600, marginBottom: 'var(--space-1)' }}>
                                            {insight.title}
                                        </div>
                                        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                            {insight.description}
                                        </p>
                                        <div
                                            style={{
                                                marginTop: 'var(--space-2)',
                                                fontSize: 'var(--font-size-sm)',
                                                color: 'var(--color-success)',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {insight.impact}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Upcoming Posts */}
                <div className="card">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-6)' }}>
                        <Calendar size={20} />
                        <h3 style={{ margin: 0 }}>Upcoming Posts</h3>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                        {upcomingPosts.length > 0 ? (
                            upcomingPosts.map((post) => (
                                <div
                                    key={post.id}
                                    style={{
                                        padding: 'var(--space-3)',
                                        background: 'var(--color-bg-secondary)',
                                        borderRadius: 'var(--radius-md)',
                                    }}
                                >
                                    <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-1)' }}>
                                        {post.creativeTitle}
                                    </div>
                                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                        {formatDate(post.scheduledDate, 'datetime')}
                                    </div>
                                    <div style={{ display: 'flex', gap: 'var(--space-1)', marginTop: 'var(--space-2)' }}>
                                        {post.platforms.map((p) => (
                                            <span key={p} className="badge badge-neutral" style={{ fontSize: '10px' }}>
                                                {p}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-secondary">No upcoming posts scheduled</p>
                        )}

                        <Link to="/schedule" className="btn btn-sm btn-primary" style={{ marginTop: 'var(--space-2)' }}>
                            <Calendar size={16} />
                            Schedule Post
                        </Link>
                    </div>
                </div>
            </div>

            {/* Recent Creatives */}
            <div className="card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
                    <h2 style={{ margin: 0 }}>Recent Creatives</h2>
                    <Link to="/library" className="btn btn-sm btn-ghost">
                        View All <ArrowRight size={16} />
                    </Link>
                </div>

                <div className="masonry-grid">
                    {recentCreatives.map((creative) => (
                        <div key={creative.id} className="card masonry-item" style={{ padding: 'var(--space-4)' }}>
                            <div
                                style={{
                                    width: '100%',
                                    background: creative.url ? 'none' : 'var(--gradient-primary)',
                                    borderRadius: 'var(--radius-lg)',
                                    marginBottom: 'var(--space-3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 'var(--font-size-3xl)',
                                    transition: 'transform var(--transition-fast)',
                                    overflow: 'hidden',
                                    position: 'relative'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                {creative.url ? (
                                    <img
                                        src={creative.url}
                                        alt={creative.title}
                                        style={{ width: '100%', height: 'auto', display: 'block' }}
                                    />
                                ) : (
                                    <div style={{ padding: 'var(--space-12)' }}>
                                        {creative.brandName.charAt(0)}
                                    </div>
                                )}
                            </div>
                            <h4 style={{ fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-1)' }}>
                                {creative.title}
                            </h4>
                            <p style={{ fontSize: 'var(--font-size-xs)', margin: 0, marginBottom: 'var(--space-2)', color: 'var(--color-text-secondary)' }}>
                                {creative.brandName}
                            </p>
                            <span className={`badge badge-${creative.status === 'scheduled' ? 'info' : creative.status === 'posted' ? 'success' : 'neutral'}`} style={{ fontSize: '10px' }}>
                                {creative.status}
                            </span>
                        </div>
                    ))}
                </div>

                <Link to="/generate" className="btn btn-primary" style={{ marginTop: 'var(--space-6)' }}>
                    <Sparkles size={20} />
                    Generate New Creative
                </Link>
            </div>
        </div>
    );
};

export default Dashboard;
